import React, { useState } from 'react';
import { Star, User, Calendar, Image as ImageIcon, Video, Play } from 'lucide-react';
import { Review } from '../../types';

interface ReviewCardProps {
  review: Review;
}

const ReviewCard: React.FC<ReviewCardProps> = ({ review }) => {
  const [showFullComment, setShowFullComment] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<any>(null);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${
              star <= rating
                ? 'text-yellow-400 fill-current'
                : 'text-gray-300 dark:text-gray-600'
            }`}
          />
        ))}
      </div>
    );
  };

  const truncateText = (text: string, maxLength: number = 300) => {
    if (text.length <= maxLength) return text;
    return text.substr(0, maxLength) + '...';
  };

  const openMediaModal = (media: any) => {
    setSelectedMedia(media);
  };

  const closeMediaModal = () => {
    setSelectedMedia(null);
  };

  return (
    <>
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
        {/* Заголовок отзыва */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {review.title}
            </h4>
            <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
              <div className="flex items-center space-x-2">
                {review.user?.avatar_url ? (
                  <img
                    src={review.user.avatar_url}
                    alt={review.user.full_name || 'Пользователь'}
                    className="w-6 h-6 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-6 h-6 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
                    <User className="w-3 h-3 text-gray-600 dark:text-gray-400" />
                  </div>
                )}
                <span>{review.user?.full_name || 'Аноним'}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Calendar className="w-4 h-4" />
                <span>{formatDate(review.created_at)}</span>
              </div>
            </div>
          </div>
          <div className="ml-4">
            {renderStars(review.rating)}
          </div>
        </div>

        {/* Текст отзыва */}
        <div className="mb-4">
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
            {showFullComment || review.comment.length <= 300
              ? review.comment
              : truncateText(review.comment)
            }
          </p>
          {review.comment.length > 300 && (
            <button
              onClick={() => setShowFullComment(!showFullComment)}
              className="text-primary-600 dark:text-primary-400 text-sm font-medium mt-2
                       hover:text-primary-700 dark:hover:text-primary-300 transition-colors duration-200"
            >
              {showFullComment ? 'Свернуть' : 'Читать полностью'}
            </button>
          )}
        </div>

        {/* Медиа */}
        {review.media && review.media.length > 0 && (
          <div className="mt-4">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {review.media.slice(0, 4).map((media, index) => (
                <div
                  key={media.id}
                  className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700
                           cursor-pointer group hover:opacity-90 transition-opacity duration-200"
                  onClick={() => openMediaModal(media)}
                >
                  {media.type === 'video' ? (
                    <>
                      <video
                        src={media.url}
                        className="w-full h-full object-cover"
                        poster={media.thumbnail || undefined}
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                        <Play className="w-8 h-8 text-white drop-shadow-lg" />
                      </div>
                      <div className="absolute bottom-1 left-1">
                        <Video className="w-4 h-4 text-white drop-shadow" />
                      </div>
                    </>
                  ) : (
                    <>
                      <img
                        src={media.url}
                        alt={media.alt || 'Изображение от покупателя'}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute bottom-1 left-1">
                        <ImageIcon className="w-4 h-4 text-white drop-shadow" />
                      </div>
                    </>
                  )}
                  
                  {/* Показать количество скрытых медиа */}
                  {index === 3 && review.media && review.media.length > 4 && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                      <span className="text-white font-semibold text-lg">
                        +{(review.media?.length || 0) - 4}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Модальное окно для просмотра медиа */}
      {selectedMedia && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4">
          <div className="relative max-w-4xl max-h-full">
            <button
              onClick={closeMediaModal}
              className="absolute top-4 right-4 z-10 p-2 bg-black bg-opacity-50 text-white rounded-full
                       hover:bg-opacity-70 transition-colors duration-200"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            {selectedMedia.type === 'video' ? (
              <video
                src={selectedMedia.url}
                controls
                autoPlay
                className="max-w-full max-h-full rounded-lg"
              />
            ) : (
              <img
                src={selectedMedia.url}
                alt={selectedMedia.alt || 'Изображение от покупателя'}
                className="max-w-full max-h-full rounded-lg"
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ReviewCard;
